import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowDown, ArrowUp, Plus, Send, Upload, X } from "lucide-react";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import ShopProfileViewer from "../../components/reportViewers/ShopProfileViewer";
import BrandIntelligenceViewer from "../../components/reportViewers/BrandIntelligenceViewer";
import {
  fetchBrandIntelligenceReport,
  getLatestReportByCollection,
  getLatestMatchingReport,
  downloadBrandPdf,
  downloadReportPdf,
  createShopProfileReport,
} from "../../utils/api/reportApi";
import pageStyles from "../../styles/PageLayout.module.css";
import ChatPage from "../ChatPage";
import styles from "./BrandPage.module.css";
import { sanitizeChatContext } from "../../utils/chatContextDrag";

const DRAG_START_MOUSE_KEY = "__zeqiBrandPageStartMouseDrag";
const DRAG_START_TOUCH_KEY = "__zeqiBrandPageStartTouchDrag";
const STORE_PROFILE_TABS = [
  { key: "main-information", label: "Main Information" },
  { key: "geography-market-coverage", label: "Geography & Market Coverage" },
  { key: "product-catalog-structure", label: "Product & Catalog Structure" },
  { key: "pricing-commercial-position", label: "Pricing & Commercial Position" },
  { key: "target-audience", label: "Target Audience" },
  { key: "brand-positioning", label: "Brand Positioning" },
  { key: "keywords-market-language", label: "Keywords & Market Language", comingSoon: true },
];

const STORE_PROFILE_REPORT_COLLECTIONS = {
  "main-information": "shop_profile_main_information",
  "geography-market-coverage": "shop_profile_geography_market_coverage",
  "product-catalog-structure": "shop_profile_product_catalog_structure",
  "pricing-commercial-position": "shop_profile_pricing_commercial_position",
  "target-audience": "shop_profile_target_audience",
  "brand-positioning": "shop_profile_brand_positioning",
  "keywords-market-language": "shop_profile_keywords_market_language",
};

const TABS = [
  { key: "brand-assets", label: "Brand assets" },
  { key: "tone-and-voice", label: "Tone & voice" },
  { key: "library", label: "Library" },
];

const LIBRARY_REPORT_DEFINITIONS = [
  {
    key: "benchmark",
    title: "Store summary PDF",
    description: "Full benchmark report rendered as viewed in the app.",
    collections: ["shop_benchmark_report"],
  },
  {
    key: "meta",
    title: "Meta ads report PDF",
    description: "Paid ads performance, campaign tables, and audience efficiency.",
    collections: ["shop_meta"],
  },
  {
    key: "google_search_console",
    title: "Google Search Console PDF",
    description: "Visibility, indexing, and search performance findings.",
    collections: [
      "google_search_console"
    ],
  },
  {
    key: "google_merchant_center",
    title: "Google Merchant Center PDF",
    description: "Product feed quality, approval state, and GMC recommendations.",
    collections: ["google_merchant_center"],
  },
  {
    key: "revenue_reality",
    title: "Revenue reality report PDF",
    description: "Unit economics, acquisition health, and scale-readiness analysis.",
    collections: [
      "revenue_reality_full_report",
    ],
  },
  {
    key: "market",
    title: "Market analysis PDF",
    description: "Market sizing, storelead benchmarks, and landscape analysis.",
    collections: [
      "shop_market_analysis"
    ],
  },
  {
    key: "target_audience",
    title: "Target audience PDF",
    description: "ICPs, audience segments, and messaging direction.",
    collections: ["shop_target_audience"],
  },
  {
    key: "competitor",
    title: "Competitor analysis PDF",
    description: "Competitor positioning, creative direction, and market comparison.",
    collections: ["shop_competitor_analysis"],
  },
  {
    key: "yearlyContent",
    title: "Monthly events calendar PDF",
    description: "Yearly events map and tactical content calendar planning.",
    collections: [
      "yearly_content_calendar"
    ],
  },
];

const LibraryTab = ({ downloadItems = [], downloadState, benchmarkLoading }) => (
  <div className={styles.tabContent}>
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Reports</h2>
      <p className={styles.sectionDesc}>Download the rendered reports and focused brand views from the latest Store summary benchmark.</p>
      {benchmarkLoading ? (
        <p className={styles.sectionDesc}>Preparing available report downloads…</p>
      ) : downloadItems.length ? (
        <div className={styles.downloadGrid}>
          {downloadItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={styles.downloadCard}
              onClick={item.onClick}
              disabled={downloadState === item.key || item.disabled}
            >
              <div>
                <p className={styles.downloadTitle}>{item.title}</p>
              </div>
              <span className={styles.downloadAction}>
                {item.disabled ? "Unavailable" : downloadState === item.key ? "Preparing…" : "Download"}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className={styles.sectionDesc}>No report downloads available yet.</p>
      )}
    </section>
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Files & documents</h2>
      <p className={styles.sectionDesc}>Upload brand documents, style guides, and reference files for Zeqi to learn from.</p>
      <div className={styles.uploadZone}>
        <Upload size={20} className={styles.uploadIcon} />
        <p className={styles.uploadLabel}>Upload files</p>
        <p className={styles.uploadHint}>PDF, DOCX, PNG, JPG · Max 20MB per file</p>
      </div>
    </section>
  </div>
);


const BrandPage = () => {
  const location = useLocation();
  const { user } = useAuthUser();
  const { selectedShop, shopData } = useShop();
  const initialTab = new URLSearchParams(location.search).get("tab") || "brand-assets";
  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;
  const shop = selectedShop || shopData || null;
  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shop?._id)
    ? shop._id
    : isMongoId(shop?.id)
      ? shop.id
      : null;

  const splitRef = useRef(null);
  const [activeTab, setActiveTab] = useState(
    initialTab === "store-profile" || TABS.find((t) => t.key === initialTab)
      ? initialTab
      : "brand-assets",
  );
  const [activeProfileTab, setActiveProfileTab] = useState("main-information");
  const [topFlex, setTopFlex] = useState(0.5);
  const [chatPaneState, setChatPaneState] = useState("default");
  const [dismissedContextKeys, setDismissedContextKeys] = useState([]);
  const [chatFocusContext, setChatFocusContext] = useState(null);
  const [benchmarkReport, setBenchmarkReport] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkError, setBenchmarkError] = useState(null);
  const [brandReport, setBrandReport] = useState(null);
  const [brandReportLoading, setBrandReportLoading] = useState(false);
  const [brandReportError, setBrandReportError] = useState(null);
  const [downloadState, setDownloadState] = useState("");
  const [libraryReportMap, setLibraryReportMap] = useState({});
  const [libraryReportsLoading, setLibraryReportsLoading] = useState(false);
  const [storeProfileReports, setStoreProfileReports] = useState({});
  const [storeProfileLoading, setStoreProfileLoading] = useState({});
  const [storeProfileErrors, setStoreProfileErrors] = useState({});
  const [keywordsInput, setKeywordsInput] = useState("");
  const [keywordsGenerating, setKeywordsGenerating] = useState(false);
  const isChatClosed = chatPaneState === "minimized";
  const handleExpandChat = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "minimized") return "default";
      if (current === "default") return "maximized";
      return "default";
    });
  }, []);
  const handleExpandReports = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "maximized") return "default";
      if (current === "default") return "minimized";
      return "minimized";
    });
    setTopFlex((current) => Math.max(current, 0.72));
  }, []);

  useEffect(() => {
    let active = true;
    if (!authToken || !userId || !shopId) return undefined;

    setBenchmarkLoading(true);
    setBenchmarkError(null);

    getLatestReportByCollection({
      collection: "shop_benchmark_report",
      userId,
      shopId,
      authToken,
    })
      .then((response) => {
        if (!active) return;
        setBenchmarkReport(response?.report || null);
      })
      .catch((error) => {
        if (!active) return;
        setBenchmarkError(error);
        setBenchmarkReport(null);
      })
      .finally(() => {
        if (!active) return;
        setBenchmarkLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authToken, userId, shopId]);

  useEffect(() => {
    let active = true;
    if (!authToken || !userId || !shopId) return undefined;

    setBrandReportLoading(true);
    setBrandReportError(null);

    fetchBrandIntelligenceReport({ userId, shopId, authToken })
      .then((response) => {
        if (!active) return;
        setBrandReport(response?.report || null);
      })
      .catch((error) => {
        if (!active) return;
        setBrandReportError(error);
        setBrandReport(null);
      })
      .finally(() => {
        if (!active) return;
        setBrandReportLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authToken, userId, shopId]);

  useEffect(() => {
    const nextTab = new URLSearchParams(location.search).get("tab") || "brand-assets";
    if (nextTab === "store-profile" || TABS.find((t) => t.key === nextTab)) {
      setActiveTab(nextTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (activeTab !== "store-profile") {
      setActiveProfileTab("main-information");
    }
  }, [activeTab]);

  useEffect(() => {
    setChatFocusContext(null);
  }, [activeTab, activeProfileTab]);

  useEffect(() => {
    let active = true;
    if (!authToken || !userId || !shopId) return undefined;

    setLibraryReportsLoading(true);

    Promise.all(
      LIBRARY_REPORT_DEFINITIONS.map(async (definition) => {
        try {
          const response = await getLatestMatchingReport({
            userId,
            shopId,
            collections: definition.collections,
            authToken,
          });
          return [definition.key, response?.report || null];
        } catch (error) {
          console.warn("[BrandPage] library lookup failed:", {
            key: definition.key,
            message: error?.message || String(error),
          });
          return [definition.key, null];
        }
      })
    )
      .then((entries) => {
        if (!active) return;
        setLibraryReportMap(Object.fromEntries(entries));
      })
      .finally(() => {
        if (!active) return;
        setLibraryReportsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authToken, shopId, userId]);

  useEffect(() => {
    let active = true;
    if (activeTab !== "store-profile" || !authToken || !userId || !shopId) return undefined;
    const collection = STORE_PROFILE_REPORT_COLLECTIONS[activeProfileTab];
    if (!collection) return undefined;
    if (storeProfileReports[activeProfileTab] !== undefined) return undefined;

    setStoreProfileLoading((prev) => ({ ...prev, [activeProfileTab]: true }));
    setStoreProfileErrors((prev) => ({ ...prev, [activeProfileTab]: null }));

    getLatestReportByCollection({ collection, userId, shopId, authToken })
      .then((response) => {
        if (!active) return;
        setStoreProfileReports((prev) => ({ ...prev, [activeProfileTab]: response?.report || null }));
      })
      .catch((error) => {
        if (!active) return;
        setStoreProfileErrors((prev) => ({ ...prev, [activeProfileTab]: error }));
        setStoreProfileReports((prev) => ({ ...prev, [activeProfileTab]: null }));
      })
      .finally(() => {
        if (!active) return;
        setStoreProfileLoading((prev) => ({ ...prev, [activeProfileTab]: false }));
      });

    return () => { active = false; };
  }, [activeTab, activeProfileTab, authToken, userId, shopId, storeProfileReports]);

  const baseChatContextTrail = useMemo(
    () => {
      const tabLabel = STORE_PROFILE_TABS.find((t) => t.key === activeProfileTab)?.label || "Store Profile";
      const activeStoreReport = activeTab === "store-profile" ? storeProfileReports[activeProfileTab] : null;
      const activeStoreReportId = activeStoreReport?._id || activeStoreReport?.id;
      const activeStoreCollection = STORE_PROFILE_REPORT_COLLECTIONS[activeProfileTab] || null;

      return [
        { label: "Brand", type: "channel", key: "brand" },
        brandReport
          ? {
              label: brandReport?.name || "Brand Intelligence",
              type: "report",
              key: `report:${brandReport?._id || brandReport?.id || "brand-intelligence"}`,
              reportId: String(brandReport?._id || brandReport?.id || ""),
              collectionName: brandReport?.data?.collection_name || "brand_intelligence_report",
            }
          : benchmarkReport
            ? {
                label: benchmarkReport?.name || "Store Summary",
                type: "report",
                key: `report:${benchmarkReport?._id || benchmarkReport?.id || "benchmark"}`,
                reportId: String(benchmarkReport?._id || benchmarkReport?.id || ""),
                collectionName: benchmarkReport?.data?.collection_name || "shop_benchmark_report",
              }
            : null,
        activeTab === "brand-assets"
          ? { label: "Brand assets", type: "knowledge", key: "brand-assets" }
          : activeTab === "store-profile"
            ? activeStoreReportId
              ? {
                  label: tabLabel,
                  type: "report",
                  key: `store-profile:${activeProfileTab}`,
                  reportId: String(activeStoreReportId),
                  collectionName: activeStoreCollection,
                }
              : {
                  label: tabLabel,
                  type: "knowledge",
                  key: `store-profile:${activeProfileTab}`,
                  collectionName: activeStoreCollection,
                }
            : activeTab === "tone-and-voice"
              ? { label: "Tone & voice", type: "knowledge", key: "tone-and-voice" }
              : null,
      ].filter(Boolean);
    },
    [activeProfileTab, activeTab, benchmarkReport, brandReport, storeProfileReports],
  );

  const visibleChatContextTrail = useMemo(() => {
    const next = baseChatContextTrail.filter(
      (item) => !dismissedContextKeys.includes(item.key),
    );
    return next.length ? next : [{ label: "Main", type: "main", key: "main" }];
  }, [baseChatContextTrail, dismissedContextKeys]);

  const chatContextTrail = useMemo(() => {
    const next = [...visibleChatContextTrail];
    if (
      chatFocusContext?.key &&
      !dismissedContextKeys.includes(chatFocusContext.key) &&
      !next.some((item) => item.key === chatFocusContext.key)
    ) {
      next.push(chatFocusContext);
    }
    return next;
  }, [visibleChatContextTrail, chatFocusContext, dismissedContextKeys]);

  const activeChatContext = useMemo(
    () => sanitizeChatContext(chatContextTrail) || { label: "Main", type: "main", key: "main" },
    [chatContextTrail],
  );

  const chatSuggestions = useMemo(() => {
    const faqByTab = {
      "brand-assets": [
        "What brand assets do I have?",
        "How can I strengthen my brand identity?",
        "What brand guidelines should I create?",
      ],
      "tone-and-voice": [
        "What's my current brand tone and voice?",
        "How should I write product descriptions?",
        "What messaging style works best for my audience?",
      ],
      library: [
        "What reports do I have available?",
        "Give me a summary of my store benchmark report",
        "What are the key findings from my Meta ads report?",
      ],
      "store-profile:main-information": [
        "What does my store profile say?",
        "How complete is my brand profile?",
        "What key business information should I update?",
      ],
      "store-profile:geography-market-coverage": [
        "What markets am I currently targeting?",
        "How can I expand my market coverage?",
        "Which geographies have the most potential for my brand?",
      ],
      "store-profile:product-catalog-structure": [
        "How is my product catalog structured?",
        "What product categories am I missing?",
        "How should I organize my catalog for better performance?",
      ],
      "store-profile:pricing-commercial-position": [
        "How competitive is my pricing?",
        "What's my price positioning in the market?",
        "How can I improve my commercial positioning?",
      ],
      "store-profile:target-audience": [
        "Who is my target audience?",
        "How can I refine my audience targeting?",
        "What segments should I focus on?",
      ],
      "store-profile:brand-positioning": [
        "How is my brand positioned in the market?",
        "How do I differentiate from competitors?",
        "What makes my brand unique?",
      ],
      "store-profile:keywords-market-language": [
        "What keywords should I be targeting?",
        "How can I improve my SEO strategy?",
        "What language resonates with my market?",
      ],
    };

    const contextKey = activeTab === "store-profile"
      ? `store-profile:${activeProfileTab}`
      : activeTab;
    const faqs = faqByTab[contextKey] || [
      "What can you tell me about my brand?",
      "How's my store performing?",
      "What should I focus on next?",
    ];
    return faqs.map((text, index) => ({ id: `brand-${activeTab}-${index}`, text, theme: "info" }));
  }, [activeTab, activeProfileTab]);

  const resetSpecificContext = useCallback((contextKey, contextLabel) => {
    setDismissedContextKeys((current) =>
      current.includes(contextKey) ? current : [...current, contextKey],
    );
    if (contextLabel) {
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-context-notification", {
          detail: { type: "removed", label: contextLabel, key: contextKey },
        }),
      );
    }
  }, []);

  const startResize = useCallback((startY) => {
    const shell = splitRef.current;
    if (!shell) return;
    const shellHeight = shell.getBoundingClientRect().height || 1;
    const initialTopHeight = shellHeight * topFlex;

    const updateFromPointer = (clientY) => {
      const delta = clientY - startY;
      const nextTopHeight = Math.min(Math.max(initialTopHeight + delta, 120), shellHeight - 120);
      setTopFlex(nextTopHeight / shellHeight);
      setChatPaneState("default");
    };

    const stopMouseDrag = () => {
      window.removeEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
      window.removeEventListener("mouseup", stopMouseDrag);
      delete window[DRAG_START_MOUSE_KEY];
    };
    const stopTouchDrag = () => {
      window.removeEventListener("touchmove", window[DRAG_START_TOUCH_KEY]);
      window.removeEventListener("touchend", stopTouchDrag);
      delete window[DRAG_START_TOUCH_KEY];
    };

    window[DRAG_START_MOUSE_KEY] = (e) => updateFromPointer(e.clientY);
    window[DRAG_START_TOUCH_KEY] = (e) => {
      const touch = e.touches[0];
      if (touch) updateFromPointer(touch.clientY);
    };

    window.addEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
    window.addEventListener("mouseup", stopMouseDrag);
    window.addEventListener("touchmove", window[DRAG_START_TOUCH_KEY], { passive: true });
    window.addEventListener("touchend", stopTouchDrag, { passive: true });
  }, [topFlex]);

  const topPaneStyle =
    chatPaneState === "maximized"
      ? { flex: "0 0 0%", minHeight: 0 }
      : isChatClosed
        ? { flex: "1 1 0%", minHeight: 0 }
        : { flex: `${topFlex} 1 0%` };
  const chatPaneStyle =
    chatPaneState === "minimized"
      ? { flex: "0 0 54px" }
      : chatPaneState === "maximized"
        ? { flex: "1 1 0%" }
        : { flex: `${1 - topFlex} 1 0%` };

  const handleBrandDownload = useCallback(async (tab) => {
    if (!authToken || !userId) return;
    setDownloadState(tab);
    try {
      await downloadBrandPdf({
        tab,
        userId,
        shopId,
        authToken,
        filename: `${tab}.pdf`,
      });
    } catch (error) {
      console.error("[BrandPage] download failed", error);
    } finally {
      setDownloadState("");
    }
  }, [authToken, shopId, userId]);

  const handleBenchmarkDownload = useCallback(async () => {
    const reportId = benchmarkReport?._id || benchmarkReport?.id;
    if (!authToken || !userId || !reportId) return;
    const stateKey = `report:${reportId}`;
    setDownloadState(stateKey);
    try {
      await downloadReportPdf({
        reportId,
        userId,
        authToken,
        filename: `${benchmarkReport?.name || "store-summary"}.pdf`,
      });
    } catch (error) {
      console.error("[BrandPage] benchmark download failed", error);
    } finally {
      setDownloadState("");
    }
  }, [authToken, benchmarkReport, userId]);

  const handleLibraryReportDownload = useCallback(async (definition) => {
    const report = libraryReportMap[definition.key];
    const reportId = report?._id || report?.id;
    if (!authToken || !userId || !reportId) return;
    const stateKey = `report:${definition.key}`;
    setDownloadState(stateKey);
    try {
      await downloadReportPdf({
        reportId,
        userId,
        authToken,
        filename: `${definition.title.replace(/\s+/g, "-").toLowerCase()}.pdf`,
      });
    } catch (error) {
      console.error("[BrandPage] report download failed", {
        key: definition.key,
        message: error?.message || String(error),
      });
    } finally {
      setDownloadState("");
    }
  }, [authToken, libraryReportMap, userId]);

  const libraryDownloadItems = useMemo(() => {
    const items = LIBRARY_REPORT_DEFINITIONS.map((definition) => {
      const report = libraryReportMap[definition.key];
      const reportId = report?._id || report?.id;
      return {
        key: `report:${definition.key}`,
        title: definition.title,
        description: report?.name || definition.description,
        onClick: () => handleLibraryReportDownload(definition),
        disabled: !reportId,
      };
    });
    items.push({
      key: "brand-assets",
      title: "Brand assets PDF",
      description: "Logo, colors, typography, components, and visual direction.",
      onClick: () => handleBrandDownload("brand-assets"),
    });
    items.push({
      key: "tone-and-voice",
      title: "Tone & voice PDF",
      description: "Tone, energy, voice attributes, and messaging guardrails.",
      onClick: () => handleBrandDownload("tone-and-voice"),
    });
    return items;
  }, [handleBrandDownload, handleLibraryReportDownload, libraryReportMap]);

  const handleGenerateKeywords = useCallback(async () => {
    if (!authToken || !userId || !shopId || keywordsGenerating) return;
    const keywords = keywordsInput.split(/[\n,]+/).map((k) => k.trim()).filter(Boolean);
    setKeywordsGenerating(true);
    setStoreProfileErrors((prev) => ({ ...prev, "keywords-market-language": null }));
    try {
      const result = await createShopProfileReport({
        userId, shopId, authToken,
        tabKey: "keywords-market-language",
        extraBody: keywords.length ? { target_market_keywords: keywords } : {},
      });
      setStoreProfileReports((prev) => ({ ...prev, "keywords-market-language": result?.data ? { data: result.data } : null }));
    } catch (err) {
      setStoreProfileErrors((prev) => ({ ...prev, "keywords-market-language": err }));
    } finally {
      setKeywordsGenerating(false);
    }
  }, [authToken, userId, shopId, keywordsInput, keywordsGenerating]);

  const topPanelContent = useMemo(() => {
    if (activeTab === "library") {
      return (
        <LibraryTab
          downloadItems={libraryDownloadItems}
          downloadState={downloadState}
          benchmarkLoading={benchmarkLoading || libraryReportsLoading}
        />
      );
    }

    if (activeTab === "store-profile") {
      const activeTabMeta = STORE_PROFILE_TABS.find((t) => t.key === activeProfileTab) || STORE_PROFILE_TABS[0];
      const isLoadingTab = Boolean(storeProfileLoading[activeProfileTab]);
      const tabReport = storeProfileReports[activeProfileTab];
      const tabError = storeProfileErrors[activeProfileTab];
      const isKeywordsTab = activeProfileTab === "keywords-market-language";
      const keywordsReport = isKeywordsTab && tabReport
        ? (tabReport?.data?.fields || []).some((f) => f.key === "primary_market_theme" || f.key === "final_market_keyword_set")
          ? tabReport : null
        : tabReport;
      return (
        <div className={styles.tabContent}>
          <div className={styles.profileShell}>
            {isKeywordsTab && (
              <section className={styles.profileSection} style={{ paddingBottom: 0 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--tm)" }}>
                    Target market keywords
                  </label>
                  <textarea
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    placeholder={"e.g. baby clothing, organic cotton, eco-friendly, newborn gifts\nOne keyword or phrase per line, or comma-separated."}
                    rows={4}
                    style={{ width: "100%", resize: "vertical", fontSize: 13, padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bd)", background: "var(--bg-sf)", color: "var(--tp)", fontFamily: "inherit", boxSizing: "border-box" }}
                  />
                  <button
                    onClick={handleGenerateKeywords}
                    disabled={keywordsGenerating}
                    style={{ alignSelf: "flex-start", padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--tp)", color: "var(--bg-app)", fontWeight: 700, fontSize: 13, cursor: keywordsGenerating ? "not-allowed" : "pointer", opacity: keywordsGenerating ? 0.6 : 1 }}
                  >
                    {keywordsGenerating ? "Analyzing…" : "Analyze"}
                  </button>
                </div>
              </section>
            )}
            <section className={styles.profileSection}>
              {isLoadingTab || keywordsGenerating ? (
                <p className={styles.sectionDesc}>{keywordsGenerating ? "Analyzing market language…" : "Loading report…"}</p>
              ) : tabError ? (
                <p className={styles.sectionDesc}>
                  {tabError?.message || "Could not load this report section."}
                </p>
              ) : (
                <ShopProfileViewer report={isKeywordsTab ? keywordsReport : tabReport} tabKey={activeProfileTab} />
              )}
            </section>
          </div>
        </div>
      );
    }

    if (brandReportLoading) {
      return (
        <div className={styles.tabContent}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Loading Brand Intelligence</h2>
            <p className={styles.sectionDesc}>Fetching your brand assets and tone analysis.</p>
          </section>
        </div>
      );
    }

    if (brandReportError) {
      return (
        <div className={styles.tabContent}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Unable to load brand report</h2>
            <p className={styles.sectionDesc}>
              {brandReportError?.message || "The Brand Intelligence report could not be loaded."}
            </p>
          </section>
        </div>
      );
    }

    if (!brandReport?.data) {
      return (
        <div className={styles.tabContent}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Brand Intelligence pending</h2>
            <p className={styles.sectionDesc}>
              Your brand intelligence report is generated automatically when you connect Shopify. Check back shortly after your store is connected.
            </p>
          </section>
        </div>
      );
    }

    if (activeTab === "brand-assets") {
      return <BrandIntelligenceViewer data={brandReport.data} view="brand-assets" />;
    }

    if (activeTab === "tone-and-voice") {
      return <BrandIntelligenceViewer data={brandReport.data} view="tone-and-voice" />;
    }

    return null;
  }, [activeProfileTab, activeTab, brandReport, brandReportError, brandReportLoading, downloadState, handleGenerateKeywords, keywordsGenerating, keywordsInput, libraryDownloadItems, libraryReportsLoading, setKeywordsInput, shopId, storeProfileErrors, storeProfileLoading, storeProfileReports, userId]);

  const visibleTabs = activeTab === "store-profile" ? STORE_PROFILE_TABS : TABS;

  return (
    <section className={`${pageStyles.page} ${styles.wrapper}`} data-page="brand">
      <div ref={splitRef} className={styles.splitShell}>
        {/* Top panel — tabs + content */}
        <div
          className={`${styles.panelPane} ${
            chatPaneState === "maximized" ? styles.panelPaneHidden : ""
          }`}
          style={topPaneStyle}
        >
          <div className={styles.tabBar}>
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`${styles.tabBtn} ${
                  (activeTab === "store-profile"
                    ? activeProfileTab === tab.key
                    : activeTab === tab.key)
                    ? styles.tabBtnActive
                    : ""
                } ${tab.comingSoon ? styles.tabBtnSoon : ""}`}
                onClick={() => {
                  if (tab.comingSoon) return;
                  if (activeTab === "store-profile") {
                    setActiveProfileTab(tab.key);
                    return;
                  }
                  setActiveTab(tab.key);
                }}
                disabled={tab.comingSoon}
              >
                {tab.label}
                {tab.comingSoon ? (
                  <span className={styles.inlineSoonBadge}>Coming Soon</span>
                ) : null}
              </button>
            ))}
          </div>
          <div className={styles.panelBody}>
            {topPanelContent}
          </div>
        </div>

        {/* Divider */}
        {!isChatClosed ? <div className={styles.paneDivider} aria-hidden="true">
          <button
            type="button"
            className={styles.dragHandle}
            onMouseDown={(e) => startResize(e.clientY)}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              if (touch) startResize(touch.clientY);
            }}
            aria-label="Resize brand chat"
          />
          <div className={styles.dividerControls}>
            <div className={styles.chatTitleWrap}>
              <span className={styles.chatTitle}>Zeqi chat</span>
              <div className={styles.chatContextTrail}>
                {chatContextTrail.map((context) => {
                  const isDismissible = context.key !== "main";
                  return (
                    <span key={context.key} className={styles.chatContextPill}>
                      <span className={styles.chatContextPillText}>{context.label}</span>
                      {isDismissible ? (
                        <button
                          type="button"
                          className={styles.chatContextPillReset}
                          onClick={() => resetSpecificContext(context.key, context.label)}
                          aria-label={`Clear ${context.label} context`}
                        >
                          <X size={12} strokeWidth={2} />
                        </button>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className={styles.controlButtons}>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleExpandChat}
                aria-label="Expand brand chat"
              >
                <ArrowUp size={16} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleExpandReports}
                aria-label="Restore brand chat"
              >
                <ArrowDown size={16} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                className={styles.controlButton}
                onClick={() => setChatPaneState("minimized")}
                aria-label="Minimise brand chat"
              >
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div> : null}

        {/* Chat pane */}
        {!isChatClosed ? <div
          className={`${styles.chatPane} ${
            chatPaneState === "minimized"
              ? styles.chatPaneMinimized
              : chatPaneState === "maximized"
                ? styles.chatPaneMaximized
                : ""
          }`}
          style={chatPaneStyle}
          >
            <div className={styles.chatBody}>
            <ChatPage
              inline
              variant="inline"
              channelId="brand"
              chatContext={activeChatContext}
              chatContextTrail={chatContextTrail}
              onContextResolved={(nextContext) => {
                if (nextContext?.label) {
                  setChatFocusContext(nextContext);
                }
              }}
              suggestionsOverride={chatSuggestions}
              inputPlaceholder={`Ask Zeqi about ${activeChatContext?.label || "Main"}...`}
            />
            </div>
        </div> : null}
      </div>
      {isChatClosed ? (
        <div className={styles.chatReopenDock}>
          <button
            type="button"
            className={styles.chatReopenField}
            onClick={() => setChatPaneState("default")}
            aria-label="Reopen Zeqi chat"
            title="Reopen Zeqi chat"
          >
            <span className={styles.chatReopenFieldIcon}>
              <Plus size={18} strokeWidth={1.9} />
            </span>
            <span className={styles.chatReopenFieldText}>
              Ask Zeqi about {activeChatContext?.label || "Main"}...
            </span>
            <span className={styles.chatReopenFieldSend}>
              <Send size={16} strokeWidth={1.9} />
            </span>
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default BrandPage;
